import React from "react";
import { withRouter } from "react-router-dom";
import { withI18n } from "react-i18next";
import styled, { css } from "styled-components";
import TextField from "@material-ui/core/TextField";
import { client } from "shared/apiClient";
import * as SharedStyle from "../../common/SharedStyledComponents";

import { CWBAPI, CWBLoadingCrest } from "cwb-react";
import { validateEmail, redirectToLogin } from "shared/helpers";
import qs from "query-string";
import PwdResetFailed from "./components/PwdResetFailed";
import PwdAlreadyReset from "./components/PwdAlreadyReset";
import TokenExpired from "./components/TokenExpired";
import LegalSection from "../../common/LegalSection";

import Button from "common/Button";
import CWBLabsFooter from "common/CWBLabsFooter";
import HorizontalDivider from "common/HorizontalDivider";
import Select from "common/Select";
import TextInput from "common/TextInput";
import Typography from "common/Typography";
import EyeClosedIcon from "../../images/eye-closed.svg";
import EyeOpenIcon from "../../images/eye-open.svg";

const PwdMinLength = 6;

class ResetPwd extends React.Component {
  state = {
    user: {
      email: "",
      password: "",
      confirmPassword: ""
    },
    isLoading: false,
    hasTokenExpired: false,
    hasPwdAlreadyReset: false,
    hasPwdResetFail: false,
    code: "",
    serverSideEmail: false,
    error: {
      message: "",
      errors: {}
    },
    clientError: {
      email: "",
      password: "",
      confirmPassword: ""
    },
    isPassHidden: true,
    isConfirmHidden: true,
  };

  async componentWillMount() {
    let api = await import(`cwb-react/dist/${CWB_API_FILE}`);
    client.api = new CWBAPI(api.default, CWB_API_ENV, CWB_USE_MOCK_ENV);

    let { code, email } = this.getQueryParams();
    if (!code || !email) {
      this.setPwdFail();
      return;
    }
    if (!validateEmail(email)) {
      code = decodeURIComponent(code);
      email = decodeURIComponent(email);
      if (!validateEmail(email)) {
        this.setPwdFail();
      }
    }
    const decodedCode = decodeURIComponent(code);
    this.setState({ user: { ...this.state.user, email }, code: decodedCode, serverSideEmail: true });

    this.checkResetToken();
  }

  getQueryParams = () => {
    if (
      this.props.location &&
      this.props.location.search &&
      this.props.location.search.length > 1
    ) {
      return qs.parse(this.props.location.search);
    }
    return {};
  };

  checkResetToken = async () => {
    let resp;
    try {
      const { user, code } = this.state;
      resp = await client.api.user.checkResetToken(user.email, code);
      this.handleCheckResetResponse(resp);
    } catch (error) {
      console.error(error);
      this.setPwdFail();
    }
  };

  handleCheckResetResponse = resp => {
    if (!resp || !resp.data) {
      this.setPwdFail();
      return;
    }
    const o = resp.data;

    if (o.expired) this.setState({ hasTokenExpired: true });
    else if (o.alreadyUsed) this.setState({ hasPwdAlreadyReset: true });
    else if (!o.success) this.setPwdFail();
  };

  onChangeField = e => {
    const { name, value } = e.target;
    this.setState({ clientError: null });
    this.setState(oldState => {
      return {
        user: {
          ...oldState.user,
          [name]: value
        }
      };
    });
  };

  setServerError = serverError => {
    this.setState({
      serverError
    });
  };

  setClientError = clientError => {
    this.setState({
      clientError
    });
  };

  setPwdFail = () => this.setState({ hasPwdResetFail: true });

  onKeyPress = event => {
    if (event.key === "Enter") {
      this.onReset();
      event.preventDefault();
    }
  };

  setIsLoading = isLoading => {
    this.setState({
      isLoading
    });
  };

  onReset = async () => {
    this.setIsLoading(true);
    this.setServerError(null);
    this.setClientError(null);

    if (!this.checkValidation()) {
      this.setIsLoading(false);
      return;
    }

    let resp;
    try {
      const { user, code } = this.state;
      resp = await client.api.user.resetPassword(
        user.email,
        code,
        user.password,
        user.confirmPassword
      );
      this.handleResetPwdResponse(resp);
    } catch (error) {
      console.log(error);
      if (error.response && error.response.data && (error.response.data.errors || (error.response.data.error && error.response.data.message)))
        this.setServerError(error.response.data);
      else this.setPwdFail();
    } finally {
      this.setIsLoading(false);
    }
  };

  handleResetPwdResponse = resp => {
    if (!resp || !resp.data) {
      this.setState({ hasPwdResetFail: true });
      return;
    }
    const o = resp.data;
    if (!o || !o.success) {
      this.setState({ hasPwdResetFail: true });
      if (o && (o.errors || (o.error && o.message)))
        this.setServerError(o);
    } else redirectToLogin();
  };

  shouldBeDisabled = () => {
    const { user, isLoading } = this.state;
    return (
      user.password.length < PwdMinLength ||
      user.confirmPassword.length < PwdMinLength ||
      user.email.length === 0 ||
      isLoading
    );
  };

  checkValidation = () => {
    const { user } = this.state;
    const { t } = this.props;
    if (!validateEmail(user.email)) {
      this.setState({
        clientError: { ...this.state.clientError, email: t("Invalid email") }
      });
      return false;
    }
    if (user.password !== user.confirmPassword) {
      this.setState({
        clientError: {
          ...this.state.clientError,
          confirmPassword: t("Password and confirm password do not match")
        }
      });
      return false;
    }
    return true;
  };

  render() {
    const {
      user,
      hasTokenExpired,
      hasPwdResetFail,
      hasPwdAlreadyReset,
      serverError,
      clientError,
      code,
      serverSideEmail
    } = this.state;
    const { t } = this.props;

    if (hasTokenExpired) return <TokenExpired />;
    if (hasPwdAlreadyReset) return <PwdAlreadyReset />;
    if (hasPwdResetFail) {
      return <PwdResetFailed code={code} userEmail={user.email} />;
    }

    const isCWBLabs = qs.parse(location.search).cwblabs;

    return isCWBLabs ? (
      <Base isCWBLabs>
        <Card>
          <Typography align="center" component="h1" variant="h3">
            {t("Reset Password")}
          </Typography>
          <HorizontalDivider />
          <TextInput
            error={Boolean(clientError && clientError.email)}
            errorMessage={clientError && clientError.email}
            label={t("Email")}
            name="email"
            value={user.email}
            onChange={this.onChangeField}
            onKeyPress={this.onKeyPress}
            disabled={(serverSideEmail && user.email) ? true : false}
          />
          <TextInput
            error={Boolean(clientError && clientError.password)}
            errorMessage={clientError && clientError.password}
            label={t("New Password (min. 6 characters)")}
            name="password"
            autoComplete="off"
            type="password"
            value={user.password}
            onChange={this.onChangeField}
            onKeyPress={this.onKeyPress}
          />
          <TextInput
            error={Boolean(clientError && clientError.confirmPassword)}
            errorMessage={clientError && clientError.confirmPassword}
            label={t("Confirm Password  (min. 6 characters)")}
            name="confirmPassword"
            type="password"
            value={user.confirmPassword}
            onChange={this.onChangeField}
            onKeyPress={this.onKeyPress}
            autoComplete="off"
          />
          <StyledButton disabled={this.shouldBeDisabled()} onClick={this.onReset}>
            {t("Reset Password")}
          </StyledButton>
          {serverError && serverError.errors && serverError.errors[0] && (
            <Typography color="error" component="span" variant="caption">
              {serverError.errors[0].message || serverError.errors[0]}
            </Typography>
          )}
          {serverError && serverError.error && serverError.message && (
            <Typography color="error" component="span" variant="caption">
              {serverError.message}
            </Typography>
          )}
        </Card>
        <CWBLabsFooter />
      </Base>
    ) : (
      <Base>
        <Wrapper>
          <SharedStyle.FormWrapper>
            <SharedStyle.FormHeader>{t("RESET PASSWORD")}</SharedStyle.FormHeader>
            <SharedStyle.FormBody>
              <SharedStyle.StyledLabel>{t("Email")}</SharedStyle.StyledLabel>
              <SharedStyle.StyledInput
                name="email"
                className="cwb-material-text-input"
                value={user.email}
                onKeyPress={this.onKeyPress}
                onChange={this.onChangeField}
                disabled={(serverSideEmail && user.email) ? true : false}
              />
              <ErrorText>
                <span>{clientError && clientError.email}</span>
              </ErrorText>
              <SharedStyle.StyledLabel>
                {t("New Password (min. 6 characters)")}
              </SharedStyle.StyledLabel>
              
              <SharedStyle.PassWrapper>
                <SharedStyle.StyledInput
                  name="password"
                  autoComplete="off"
                  value={user.password}
                  onChange={this.onChangeField}
                  onKeyPress={this.onKeyPress}
                  type={this.state.isPassHidden ? "password" : "text"}
                />
                <SharedStyle.PassHideButton title={t("Show password")} onClick={() => this.setState(({ isPassHidden }) => ({ isPassHidden: !isPassHidden }))}>
                  {this.state.isPassHidden
                    ? <SharedStyle.PassHideImg src={EyeClosedIcon} />
                    : <SharedStyle.PassHideImg src={EyeOpenIcon} />
                  }
                </SharedStyle.PassHideButton>
              </SharedStyle.PassWrapper>

              <ErrorText>
                <span>{clientError && clientError.password}</span>
              </ErrorText>
              <SharedStyle.StyledLabel>
                {t("Confirm Password  (min. 6 characters)")}
              </SharedStyle.StyledLabel>
              <SharedStyle.PassWrapper>
                <SharedStyle.StyledInput
                  name="confirmPassword"
                  value={user.confirmPassword}
                  onChange={this.onChangeField}
                  onKeyPress={this.onKeyPress}
                  type={this.state.isConfirmHidden ? "password" : "text"}
                  autoComplete="off"
                />
                <SharedStyle.PassHideButton title={t("Show password")} onClick={() => this.setState(({ isConfirmHidden }) => ({ isConfirmHidden: !isConfirmHidden }))}>
                  {this.state.isConfirmHidden
                    ? <SharedStyle.PassHideImg src={EyeClosedIcon} />
                    : <SharedStyle.PassHideImg src={EyeOpenIcon} />
                  }
                </SharedStyle.PassHideButton>
              </SharedStyle.PassWrapper>
              <ErrorText>
                <span>{clientError && clientError.confirmPassword}</span>
              </ErrorText>
              <SharedStyle.SubmitButton
                className="cwb-btn-primary"
                disabled={this.shouldBeDisabled()}
                onClick={this.onReset}
              >
                {t("Reset Password")}
              </SharedStyle.SubmitButton>
              <ErrorText>
              {serverError && serverError.errors && serverError.errors[0] && (
                <span>
                  {serverError.errors[0].message || serverError.errors[0]}
                </span>
              )}
              {serverError && serverError.error && serverError.message && (
                <span>
                  {serverError.message}
                </span>
              )}
              </ErrorText>
            </SharedStyle.FormBody>
            <LegalSection />
          </SharedStyle.FormWrapper>

          {/* {serverError && serverError.message && (
          <div className="alert alert-danger" style={{ marginTop: "45px" }}>
            <strong>{t("We could not process your request.")}</strong>
            {serverError.errors.map(
              error => error.message && <p key={error.field}>{error.message}</p>
            )}
          </div>
        )} */}
        </Wrapper>
      </Base>
    );
  }
}

export default withRouter(withI18n()(ResetPwd));

const Base = styled.div`
  width: 100%;
  height: calc(100vh - 60px);
  padding: 10vh 0;

  @media (max-width: 375px) {
    padding: 5vh 0;
  }

  ${(p) => p.isCWBLabs && css`
    height: calc(100vh - 80px);
    background-color: #040F1C;
  `}
`;

const Wrapper = styled.div`
  width: 610px;
  margin: auto;
  background: white;

  @media (max-width: 475px) {
    width: 100%;
  }
`;

const ErrorText = styled.div`
  width: 100%;
  font-size: 12px;
  letter-spacing: 0.21px;
  line-height: 15px;
  margin-top: 8px;

  span {
    font-weight: normal;
    color: red !important;
  }
`;

const Card = styled.div`
  width: 400px;
  display: flex;
  flex-flow: column nowrap;
  margin: auto;
  padding: 24px 18px;
  background-color: #FFFFFF;
  border-top: 6px solid #02B8F9;
  border-radius: 4px;

  & > label {
    margin-top: 8px;
  }

  @media (max-width: 375px) {
    width: unset;
    margin: 0 16px;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 24px;

  & + span {
    margin-top: 8px;
  }
`;


SharedStyle.PassWrapper
SharedStyle.PassHideButton
SharedStyle.PassHideImg